import { Elements } from '@stripe/react-stripe-js';
import {
  Appearance,
  StripeElementsOptions,
  StripePaymentElementChangeEvent,
  loadStripe,
} from '@stripe/stripe-js';
import { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import PaymentInformationContent, {
  PaymentFormRef,
  PaymentResult,
} from './PaymentInformationContent';
import { getSelectedSubscription } from '../../../../core/store/slices/subscription.slice';
import { useSelector } from 'react-redux';
import { useSetupIntentQuery } from '../../../billing/api/billing.api';

interface PaymentInformationProps {
  onPaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
}

export interface PaymentFormHandles {
  submitPayment: () => Promise<PaymentResult>;
  resetPaymentElement: () => void;
  discount: number;
  setPromoCode?: (code: string) => void;
}

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const PaymentInformation = forwardRef<PaymentFormHandles, PaymentInformationProps>(
  ({ onPaymentElementChange }, ref) => {
    const [isReady, setIsReady] = useState<boolean>(false);
    const [promoCode, setPromoCode] = useState<string | null>(null);
    const paymentFormRef = useRef<PaymentFormRef>(null);
    const { data: setupIntent, isLoading } = useSetupIntentQuery();
    const customer_session_client_secret = setupIntent?.data?.data?.customer_session_client_secret;
    const selectedSubscription = useSelector(getSelectedSubscription);

    useImperativeHandle(ref, () => ({
      submitPayment: async () => {
        if (paymentFormRef.current) {
          if (promoCode && paymentFormRef.current.setPromoCode) {
            paymentFormRef.current.setPromoCode(promoCode);
          }
          return await paymentFormRef.current.submitPayment();
        }
        return {
          success: false,
          errorMessage: 'Payment form is not initialized',
          errorType: 'initialization_error',
        };
      },
      resetPaymentElement: () => {
        if (paymentFormRef.current) {
          paymentFormRef.current.resetPaymentElement();
        }
      },
      discount: 0,
      setPromoCode: (code: string) => {
        setPromoCode(code);
        if (paymentFormRef.current && paymentFormRef.current.setPromoCode) {
          paymentFormRef.current.setPromoCode(code);
        }
      },
    }));

    const appearance: Appearance = {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#5C5AF0',
        colorBackground: '#FFFFFF',
        colorText: '#757575',
        colorDanger: '#d50000',
        fontFamily: '"Google Sans", Roboto, RobotoDraft, Helvetica, Arial, sans-serif',
      },
      rules: {
        '.Block': {
          backgroundColor: 'var(--colorBackground)',
          boxShadow: 'none',
          padding: '12px',
        },
        '.Input': {
          padding: '12px',
        },
        '.Input:disabled, .Input--invalid:disabled': {
          color: 'lightgray',
        },
        '.Tab': {
          padding: '10px 12px 8px 12px',
          border: 'none',
        },
        '.Tab:hover': {
          border: 'none',
          boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
        },
        '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
          border: 'none',
          backgroundColor: '#fff',
          boxShadow:
            '0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
        },
        '.Label': {
          fontWeight: '500',
        },
      },
    };
    const amount = selectedSubscription?.price!;
    const elementsOptions: StripeElementsOptions = {
      appearance,
      mode: 'payment',
      amount: amount * 100,
      currency: 'usd',
      paymentMethodCreation: 'manual',
      customerSessionClientSecret: customer_session_client_secret,
      payment_method_options: {
        card: { require_cvc_recollection: true },
      },
    };

    useEffect(() => {
      if (selectedSubscription?.price && selectedSubscription?.price > 0) {
        setIsReady(true);
      }
    }, [selectedSubscription?.price]);

    return (
      isReady && (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <PaymentInformationContent
            ref={paymentFormRef}
            onPaymentElementChange={onPaymentElementChange}
          />
        </Elements>
      )
    );
  }
);

export default PaymentInformation;
